import React, { useEffect } from 'react';
import { Box, Drawer, IconButton, Tooltip, useTheme } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';

import { appBarHeight, navRailBreakpoint } from '@theme/variables';
import { touchTargetSx } from 'theme/touchTarget';
import { drawerShadow } from 'theme/shadows';

const DRAWER_WIDTH = '44rem';

interface IDetailsDrawerProps {
  open: boolean;
  heading: React.ReactNode;
  actions: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  onOpenFullPage: () => void;
}

export const DetailsDrawer: React.FC<IDetailsDrawerProps> = ({
  open,
  heading,
  actions,
  children,
  onClose,
  onOpenFullPage,
}) => {
  const theme = useTheme();

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={open}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          maxWidth: '100%',
          boxShadow: drawerShadow,
          display: 'flex',
          flexDirection: 'column',
          top: { xs: appBarHeight, [navRailBreakpoint]: 0 },
          height: {
            xs: `calc(100% - ${appBarHeight})`,
            [navRailBreakpoint]: '100%',
          },
        },
      }}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        gap={1.5}
        px={3}
        py={2}
        borderBottom={`1px solid ${theme.palette.background.divider}`}
      >
        {heading}
        <Tooltip title="Open the full page" placement="top">
          <IconButton
            size="small"
            aria-label="Open the full candidate page"
            onClick={onOpenFullPage}
            sx={touchTargetSx}
          >
            <OpenInFullIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          aria-label="Close"
          onClick={onClose}
          sx={touchTargetSx}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <Box flexGrow={1} overflow="auto">
        {children}
      </Box>
      {Boolean(actions) && (
        <Box
          position="sticky"
          bottom={0}
          sx={{ backgroundColor: 'common.white', zIndex: 1 }}
        >
          {actions}
        </Box>
      )}
    </Drawer>
  );
};
